/*
 * Agency mode as React Query hooks (draft 13). Each change refreshes every agency answer, since a task, a plan or a deal
 * shows on several pages at once (Home, the client page, the clients table).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AgencyClientDetail,
  AgencyClientRow,
  AgencyHome,
  AgreementRow,
  ClientResults,
  DeckRow,
  MarketingAccountInput,
  ProjectRow,
  ReportRow,
  TaskKind,
  TaskRow,
  TASK_VIEWS,
} from "@steward/shared";
import { api } from "./client";

const KEYS = [
  "ag-home",
  "ag-clients",
  "ag-client",
  "ag-results",
  "ag-plans",
  "ag-tasks",
  "ag-projects",
  "ag-decks",
  "pipeline",
  "quotes",
];

function useChange<V, T>(run: (v: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => Promise.all(KEYS.map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
}

export const useAgencyHome = (companyId: string) =>
  useQuery({ queryKey: ["ag-home", companyId], queryFn: () => api<AgencyHome>("GET", "agency/home") });
export const useAgencyClients = (companyId: string) =>
  useQuery({ queryKey: ["ag-clients", companyId], queryFn: () => api<AgencyClientRow[]>("GET", "agency/clients") });
export const useAgencyClient = (id: string) =>
  useQuery({ queryKey: ["ag-client", id], queryFn: () => api<AgencyClientDetail>("GET", `agency/clients/${id}`) });
export const useClientResults = (id: string) =>
  useQuery({
    queryKey: ["ag-results", id],
    queryFn: () => api<{ results: ClientResults; reports: ReportRow[] }>("GET", `agency/clients/${id}/results`),
  });
export const useResults = (companyId: string) =>
  useQuery({ queryKey: ["ag-results", companyId], queryFn: () => api<ClientResults[]>("GET", "agency/results") });
export const usePlans = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ["ag-plans", companyId], queryFn: () => api<AgreementRow[]>("GET", "agency/plans"), enabled });
export const useTasks = (companyId: string, view: (typeof TASK_VIEWS)[number], clientId?: string) =>
  useQuery({
    queryKey: ["ag-tasks", companyId, view, clientId ?? ""],
    queryFn: () => api<TaskRow[]>("GET", `agency/tasks?view=${view}${clientId ? `&clientId=${clientId}` : ""}`),
  });
export const useProjects = (companyId: string) =>
  useQuery({ queryKey: ["ag-projects", companyId], queryFn: () => api<ProjectRow[]>("GET", "agency/projects") });
export const useTeam = (companyId: string) =>
  useQuery({
    queryKey: ["ag-team", companyId],
    queryFn: () => api<{ id: string; name: string }[]>("GET", "agency/team"),
    staleTime: 60_000,
  });
export const useDecks = (leadId: string | null) =>
  useQuery({
    queryKey: ["ag-decks", leadId],
    queryFn: () => api<DeckRow[]>("GET", `agency/deals/${leadId ?? ""}/slides`),
    enabled: Boolean(leadId),
  });

export interface TaskIn {
  title: string;
  kind: TaskKind;
  dueOn: string;
  dueTime?: string | null;
  assignedTo?: string | null;
  clientId?: string | null;
  leadId?: string | null;
  projectId?: string | null;
  note?: string | null;
}
export const useAddTask = () => useChange((v: TaskIn) => api<{ id: string }>("POST", "agency/tasks", v));
export const useChangeTask = () =>
  useChange(({ id, ...v }: { id: string; done?: boolean; title?: string; dueOn?: string }) =>
    api<{ ok: boolean }>("PATCH", `agency/tasks/${id}`, v),
  );
export const useAddProject = () =>
  useChange((v: { clientId: string; name: string; kind: "retainer" | "project"; dueOn?: string | null }) =>
    api<{ id: string }>("POST", "agency/projects", v),
  );
export const useChangeProject = () =>
  useChange(({ id, ...v }: { id: string; status?: string; ownerId?: string | null }) =>
    api<{ ok: boolean }>("PATCH", `agency/projects/${id}`, v),
  );
export const useNotice = () =>
  useChange((v: { id: string; noticeOn: string | null }) =>
    v.noticeOn
      ? api<AgreementRow>("POST", `agency/plans/${v.id}/notice`, { noticeOn: v.noticeOn })
      : api<AgreementRow>("DELETE", `agency/plans/${v.id}/notice`),
  );
export const useClientOwner = () =>
  useChange((v: { id: string; ownerId: string | null }) =>
    api<{ ok: boolean }>("PUT", `agency/clients/${v.id}/owner`, { ownerId: v.ownerId }),
  );
export const useClientLink = () =>
  useChange((v: { id: string; companyId: string | null }) =>
    api<{ ok: boolean }>("PUT", `agency/clients/${v.id}/link`, { companyId: v.companyId }),
  );
/** Makes the client's marketing account. It is a new company, so the company picker's list is read again too. */
export const useMakeAccount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, ...v }: { clientId: string } & MarketingAccountInput) =>
      api<{ id: string; name: string }>("POST", `agency/clients/${clientId}/account`, v),
    onSuccess: () => Promise.all(["companies", ...KEYS].map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
};
export const useAddReport = () =>
  useChange(({ clientId, ...v }: { clientId: string; month: string } & Record<string, unknown>) =>
    api<ReportRow>("POST", `agency/clients/${clientId}/reports`, v),
  );
export const useMakeSlides = () =>
  useChange((v: { leadId: string; lookUpCompetitors: boolean }) =>
    api<DeckRow>("POST", `agency/deals/${v.leadId}/slides`, { lookUpCompetitors: v.lookUpCompetitors }),
  );
export const useProposalFromDeal = () =>
  useChange(
    (v: {
      leadId: string;
      monthlyCents: number;
      setupCents: number;
      description: string;
      extras: { description: string; monthlyCents: number }[];
    }) =>
      api<{ quoteId: string }>("POST", `agency/deals/${v.leadId}/proposal`, {
        monthlyCents: v.monthlyCents,
        setupCents: v.setupCents,
        description: v.description,
        extras: v.extras,
      }),
  );
